import { Suspense, useEffect } from "react";

import {
  PROJECTS_AREA_SEGMENT,
  PROJECTS_LIBRARY_SEGMENT,
  PROJECTS_TASKS_SEGMENT,
} from "@hightouch/lib/llm/asset-comments/paths";
import { homeRoute } from "src/components/router/constants";
import { FULLSCREEN_ROUTE_HANDLE } from "src/components/layout/fullscreen-route";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { SafariPinchContext } from "src/components/preview-canvas/safari-pinch";
import { useProjectsAccess } from "src/components/layout/nav/sections/hooks/projects";
import { PaperLoader } from "src/components/loading";
import { ProjectsBoardProvider } from "src/pages/projects/board-context";
import { CanvasPageSkeleton } from "src/pages/projects/library/canvas/canvas-skeleton";
import { LegacyPageRedirect } from "src/pages/projects/legacy-page-redirect";
import { TaskPeek } from "src/pages/projects/components/task-peek";
import {
  PROJECTS_AREA_SEGMENTS,
  PROJECTS_AD_TEMPLATES_SEGMENT,
  PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT,
  PROJECTS_EMAIL_TEMPLATES_SEGMENT,
  PROJECTS_INBOX_SEGMENT,
  PROJECTS_PATHS,
  PROJECTS_SETTINGS_SEGMENT,
} from "src/pages/projects/constants";
import { ProjectsAgentLayout } from "src/pages/projects/projects-agent-sidebar";
import {
  areaLoader,
  projectLoader,
  projectsListLoader,
  runWaitingPrefetches,
  taskLoader,
} from "src/pages/projects/prefetch";
import { AgentWorkProvider } from "src/pages/projects/use-agent-work";
import type { RouteObject } from "src/router";
import { Navigate, NoContextOutlet } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { TemplateLibraryRoute } from "src/components/template-library/template-library-route";
import { lazyNamed } from "src/utils/lazy-named";

const tasksImport = () => import("./tasks/tasks-page");
const taskDetailImport = () => import("./tasks/task-detail-page");
const projectsListImport = () => import("./projects/projects-list-page");
const projectDetailImport = () => import("./projects/project-detail-page");
const createTaskDialogImport = () => import("./components/create-task-dialog");
const settingsLayoutImport = () => import("./settings/settings-layout");
const workflowsSettingsImport = () => import("./settings/workflows-settings");
const tagsSettingsImport = () => import("./settings/tags-settings");
const libraryImport = () => import("./library/library-page");
const canvasImport = () => import("./library/canvas/canvas-page");

// Every page of the area, so entering it loads the rest in the background.
const PAGE_IMPORTS = [
  tasksImport,
  taskDetailImport,
  projectsListImport,
  projectDetailImport,
  createTaskDialogImport,
  settingsLayoutImport,
  workflowsSettingsImport,
  tagsSettingsImport,
];

const loadAreaPages = () => Promise.all(PAGE_IMPORTS.map((load) => load()));

// A template editor opens one template over the page and returns to Settings, so it needs none of the area's pages.
const EDITOR_SEGMENTS: ReadonlySet<string> = new Set([
  PROJECTS_EMAIL_TEMPLATES_SEGMENT,
  PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT,
  PROJECTS_AD_TEMPLATES_SEGMENT,
]);

export const preloads: RoutePreload[] = PROJECTS_AREA_SEGMENTS.filter(
  (segment) => !EDITOR_SEGMENTS.has(segment),
).map((segment): RoutePreload => [`/${segment}`, loadAreaPages]);

const TasksPage = lazyNamed(tasksImport, "TasksPage");
const TaskDetailPage = lazyNamed(taskDetailImport, "TaskDetailPage");
const ProjectsListPage = lazyNamed(projectsListImport, "ProjectsListPage");
const ProjectDetailPage = lazyNamed(projectDetailImport, "ProjectDetailPage");
const CreateTaskDialog = lazyNamed(createTaskDialogImport, "CreateTaskDialog");
const ProjectSettingsLayout = lazyNamed(
  settingsLayoutImport,
  "ProjectSettingsLayout",
);
const WorkflowsSettings = lazyNamed(
  workflowsSettingsImport,
  "WorkflowsSettings",
);
const TagsSettings = lazyNamed(tagsSettingsImport, "TagsSettings");
const LibraryPage = lazyNamed(libraryImport, "LibraryPage");
const CanvasPage = lazyNamed(canvasImport, "CanvasPage");
const AmpTemplateEditWorkspace = lazyNamed(
  () => import("src/pages/lifecycle-studio/templates/template-edit-workspace"),
  "AmpTemplateEditWorkspace",
);

const AmpAdTemplateEditorPage = lazyNamed(
  () => import("src/pages/ad-studio/templates/editor"),
  "AmpAdTemplateEditorPage",
);

const ProjectsRoute = ({
  withGlobalChat = true,
  preloadAreaPages = true,
}: {
  withGlobalChat?: boolean;
  preloadAreaPages?: boolean;
}) => {
  const { isEnabled, isLoading } = useProjectsAccess();

  // Queued by the loaders on a cold deep link; started here, alongside the access check and the page's chunk.
  useEffect(() => {
    runWaitingPrefetches();
  }, []);

  useEffect(() => {
    if (!isEnabled || !preloadAreaPages) return;
    const id = window.setTimeout(() => {
      // A chunk that fails here loads again, with retries, when its page opens.
      for (const load of PAGE_IMPORTS) load().catch(() => undefined);
    }, 1_000);
    return () => window.clearTimeout(id);
  }, [isEnabled, preloadAreaPages]);

  // A deep link lands before the permission check resolves; redirecting then would bounce everyone out.
  if (isLoading) {
    return <PaperLoader />;
  }
  if (!isEnabled) {
    return <Navigate to={homeRoute} replace />;
  }
  if (!withGlobalChat) return <NoContextOutlet />;
  return (
    <ProjectsBoardProvider createTaskDialog={CreateTaskDialog}>
      <AgentWorkProvider>
        <ProjectsAgentLayout>
          <NoContextOutlet />
        </ProjectsAgentLayout>
        <TaskPeek />
      </AgentWorkProvider>
    </ProjectsBoardProvider>
  );
};

// Links into the area from before each page had its own top-level URL, which mention emails still carry.
const LEGACY_PAGE_REDIRECTS: [string, string][] = [
  [PROJECTS_INBOX_SEGMENT, PROJECTS_PATHS.projects],
  [PROJECTS_TASKS_SEGMENT, PROJECTS_PATHS.tasks],
  [PROJECTS_LIBRARY_SEGMENT, PROJECTS_PATHS.library],
  ["settings", PROJECTS_PATHS.settings],
];

export const routes: RouteObject[] = [
  {
    // Template editors use their authoring session chat.
    spacesAccess: "hidden-from-limited-view",
    element: (
      <FeatureFlagRoute flagKey="appAmpV2" redirect={homeRoute}>
        <ProjectsRoute withGlobalChat={false} preloadAreaPages={false} />
      </FeatureFlagRoute>
    ),
    children: [
      {
        path: `${PROJECTS_EMAIL_TEMPLATES_SEGMENT}/:emailId`,
        spacesAccess: "hidden-from-limited-view",
        handle: FULLSCREEN_ROUTE_HANDLE,
        element: (
          <TemplateLibraryRoute category="email">
            <AmpTemplateEditWorkspace />
          </TemplateLibraryRoute>
        ),
      },
      {
        path: `${PROJECTS_EMAIL_TEMPLATE_DRAFTS_SEGMENT}/:emailId`,
        spacesAccess: "hidden-from-limited-view",
        handle: FULLSCREEN_ROUTE_HANDLE,
        element: (
          <TemplateLibraryRoute category="email">
            <AmpTemplateEditWorkspace isImportDraftRoute />
          </TemplateLibraryRoute>
        ),
      },
      {
        path: `${PROJECTS_AD_TEMPLATES_SEGMENT}/:templateId`,
        spacesAccess: "hidden-from-limited-view",
        handle: FULLSCREEN_ROUTE_HANDLE,
        element: (
          <TemplateLibraryRoute category="ads">
            <AmpAdTemplateEditorPage />
          </TemplateLibraryRoute>
        ),
      },
    ],
  },
  {
    // One layout for every page of the area, so moving between them keeps the board and the agent sidebar mounted.
    spacesAccess: "hidden-from-limited-view",
    loader: areaLoader,
    element: (
      <FeatureFlagRoute flagKey="appAmpV2" redirect={homeRoute}>
        <ProjectsRoute />
      </FeatureFlagRoute>
    ),
    children: [
      {
        path: PROJECTS_AREA_SEGMENT,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            loader: projectsListLoader,
            element: <ProjectsListPage />,
          },
          ...LEGACY_PAGE_REDIRECTS.map(
            ([segment, to]): RouteObject => ({
              // Static, so it outranks a project's `:projectId`.
              path: `${segment}/*`,
              spacesAccess: "hidden-from-limited-view",
              element: <LegacyPageRedirect to={to} />,
            }),
          ),
          {
            // A task seen from within its project, which stays open in the navigation.
            path: `:projectId/${PROJECTS_TASKS_SEGMENT}/:taskId`,
            spacesAccess: "hidden-from-limited-view",
            loader: taskLoader,
            element: <TaskDetailPage />,
          },
          {
            path: ":projectId/*",
            spacesAccess: "hidden-from-limited-view",
            loader: projectLoader,
            element: <ProjectDetailPage />,
          },
        ],
      },
      {
        path: PROJECTS_INBOX_SEGMENT,
        spacesAccess: "hidden-from-limited-view",
        // The inbox opens over the page from the nav's bell, so a link to its old page lands on the projects list.
        element: <Navigate to={PROJECTS_PATHS.projects} replace />,
      },
      {
        path: PROJECTS_TASKS_SEGMENT,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <TasksPage />,
          },
          {
            path: ":taskId",
            spacesAccess: "hidden-from-limited-view",
            loader: taskLoader,
            element: <TaskDetailPage />,
          },
        ],
      },
      {
        path: PROJECTS_LIBRARY_SEGMENT,
        spacesAccess: "hidden-from-limited-view",
        element: (
          // The canvas and the Library's file view zoom from Safari's pinch gestures; the shared editors elsewhere don't.
          <SafariPinchContext.Provider value>
            <NoContextOutlet />
          </SafariPinchContext.Provider>
        ),
        children: [
          {
            path: "",
            spacesAccess: "hidden-from-limited-view",
            element: <LibraryPage />,
            children: [
              {
                path: ":fileId",
                spacesAccess: "hidden-from-limited-view",
                element: null,
              },
              // Under the Library page's route, so moving between the New view and a folder keeps the page mounted.
              {
                path: "new",
                spacesAccess: "hidden-from-limited-view",
                element: null,
                children: [
                  {
                    path: ":fileId",
                    spacesAccess: "hidden-from-limited-view",
                    element: null,
                  },
                ],
              },
            ],
          },
          {
            path: "canvases/:canvasId",
            spacesAccess: "hidden-from-limited-view",
            handle: FULLSCREEN_ROUTE_HANDLE,
            element: (
              // Here, not in the layout, so the canvas's code loads behind its own skeleton.
              <Suspense fallback={<CanvasPageSkeleton />}>
                <CanvasPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: PROJECTS_SETTINGS_SEGMENT,
        spacesAccess: "hidden-from-limited-view",
        element: <ProjectSettingsLayout />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="workflows" replace />,
          },
          {
            path: "workflows",
            spacesAccess: "hidden-from-limited-view",
            element: <WorkflowsSettings />,
          },
          {
            path: "tags",
            spacesAccess: "hidden-from-limited-view",
            element: <TagsSettings />,
          },
        ],
      },
    ],
  },
];
